import {
  getCurrentMessage,
  getDateMessageBar,
  getNewMessageBar,
  getVisualizationMessage,
  setQuestionnaireControls
} from './transformers'
import { getMessagesContext } from './utils'

const getMessageIndex = state =>
  state.filtered.findIndex(message => message.id === state.highlight)

const setHighlight = (state, message) => {
  const highlight = message?.id || undefined
  Object.assign(state, { highlight })
}

const highlightPrevious = state => {
  if (state.highlight) {
    setHighlight(state, state.filtered[getMessageIndex(state) - 1])
  } else {
    setHighlight(state, state.filtered[state.filtered.length - 1])
  }
}

const highlightNext = state => {
  if (state.highlight) {
    setHighlight(state, state.filtered[getMessageIndex(state) + 1])
  } else {
    setHighlight(state)
  }
}

const clearHighlight = state => {
  Object.assign(state, { highlight: undefined })
}

const resetFormattedCurrentMessages = state => {
  state.formattedCurrentMessages['public'] = []
  state.formattedCurrentMessages['private'] = []
}

const setFormattedCurrentMessages = (
  state,
  {
    isPrivate,
    loggedStaffId,
    channelPersonId,
    currentChannelParticipants,
    lastSync,
    docs = []
  }
) => {
  const lastPossibleIndex = Math.max(docs.length - 1, 0)

  const currentMessages = docs.map(doc => {
    const message = doc.data()
    const isFromCurrentUser = loggedStaffId === message.userId

    return {
      ...message,
      id: doc.id,
      isFromMember: channelPersonId === message.userId,
      isFromCurrentUser,
      isLastSyncedMessage:
        !isFromCurrentUser && lastSync < message.createdAt?.seconds
    }
  })

  const formattedCurrentMessages = currentMessages.reduce(
    (messages, currentMessage, index) => {
      const oldMessage = { ...currentMessages[index - 1] }
      const hasNewMessage = messages.find(message => message?.newMessages)

      const extraMessages = [
        getVisualizationMessage({
          oldMessage,
          loggedStaffId,
          currentChannelParticipants,
          currentMessage
        }),
        getNewMessageBar({ hasNewMessage, currentMessage }),
        getDateMessageBar({ oldMessage, currentMessage })
      ].filter(Boolean)

      const message = getCurrentMessage({
        currentMessage,
        channelPersonId
      })
      messages.push(...extraMessages, ...message)

      if (index === lastPossibleIndex) {
        const visualizationMessage = getVisualizationMessage({
          oldMessage: currentMessage,
          loggedStaffId,
          currentChannelParticipants,
          currentMessage: {
            userId: loggedStaffId,
            createdAt: { seconds: new Date().getTime() }
          }
        })

        if (visualizationMessage) messages.push(visualizationMessage)
      }

      return messages
    },
    []
  )

  const formattedMessagesWithQuestionnaireControl = setQuestionnaireControls(
    formattedCurrentMessages
  )

  const array = getMessagesContext(isPrivate)
  state.formattedCurrentMessages[array] =
    formattedMessagesWithQuestionnaireControl
}

const setFilteredMessages = (state, filtered) => {
  Object.assign(state, { filtered })
}

const clearFilteredMessages = state => {
  Object.assign(state, { filtered: [], highlight: undefined })
}

const handleDraftMessage = (
  state,
  { channelId, isPrivate = false, message }
) => {
  const index = state.draft.findIndex(
    draft => draft.channelId === channelId && draft.isPrivate === isPrivate
  )
  if (index >= 0) updateDraftMessage(state, { index, message })
  else addDraftMessage(state, { channelId, isPrivate, message })
}

const handleBudMessage = (state, { channelId, message: budMessage }) => {
  const draft = state.draft.find(
    draft => draft.channelId === channelId && !draft.isPrivate
  )
  const message = [draft?.message, budMessage].filter(Boolean).join('')
  handleDraftMessage(state, { channelId, message })
}

const updateDraftMessage = (state, { index, message }) => {
  state.draft[index] = {
    ...state.draft[index],
    message
  }
}

const addDraftMessage = (state, { channelId, isPrivate, message }) => {
  state.draft.push({
    channelId,
    isPrivate,
    message
  })
}

const removeDraftMessage = (state, { channelId, isPrivate }) => {
  const index = state.draft.findIndex(
    draft => draft.channelId === channelId && draft.isPrivate === isPrivate
  )

  state.draft.splice(index, 1)
}

const setUsedMacro = (state, macro) => {
  Object.assign(state, { usedMacros: [...state.usedMacros, macro] })
}

const setIsPrivate = (state, isPrivate) => {
  Object.assign(state, { isPrivate })
}

const setMacros = (state, macros) => {
  Object.assign(state, { macros: macros })
}

const setSendingFile = (state, isSendingFile) => {
  Object.assign(state, { isSendingFile })
}

const setReplyTo = (state, message) => {
  Object.assign(state, { replyTo: message })
}

const clearReplyTo = state => {
  Object.assign(state, { replyTo: null })
}

const setMessagesListener = (state, listener) => {
  if (state.messagesListener) {
    state.messagesListener()
  }
  state.messagesListener = listener
}

const clearMessagesListener = state => {
  if (state.messagesListener) {
    state.messagesListener()
  }
  state.messagesListener = null
}

const incrementCurrentPage = state => {
  state.currentPage++
}

const resetCurrentPage = state => {
  state.currentPage = 0
}

const setEndPagination = (state, endPagination) => {
  state.endPagination = endPagination
}

export default {
  highlightPrevious,
  highlightNext,
  resetFormattedCurrentMessages,
  setFormattedCurrentMessages,
  setFilteredMessages,
  clearFilteredMessages,
  handleBudMessage,
  handleDraftMessage,
  removeDraftMessage,
  setIsPrivate,
  setUsedMacro,
  setMacros,
  clearHighlight,
  setSendingFile,
  setMessagesListener,
  clearMessagesListener,
  setReplyTo,
  clearReplyTo,
  incrementCurrentPage,
  resetCurrentPage,
  setEndPagination
}
